'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { FeedbackWithJob, JobResponse } from '@/lib/types';
import api from '@/lib/api';
import { useToast } from '@/components/Toast';
import ImageModal from '@/components/ImageModal';
import { LazyImage } from '@/components/LazyImage';

export default function FavoritesGalleryPage() {
  const [items, setItems] = useState<FeedbackWithJob[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Modal state
  const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);

  const toast = useToast();

  const fetchFavorites = useCallback(async (append = false) => {
    if (!append) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const offset = append ? items.length : 0;
      const result = await api.getFavorites({ limit: 50, offset });

      if (append) {
        setItems((prev) => [...prev, ...result.items]);
      } else {
        setItems(result.items);
      }
      setTotal(result.total);
      setHasMore(result.has_more);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [items.length]);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const handleUnlike = async (imagePath: string) => {
    try {
      await api.removeFeedback(imagePath);
      setItems((prev) => prev.filter((item) => item.image_path !== imagePath));
      setTotal((prev) => prev - 1);
      toast.success('Removed from favorites');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  // Get all jobs for navigation in modal
  const allJobs = items
    .filter((item) => item.job !== null)
    .map((item) => item.job as JobResponse);

  const handleNavigate = (job: JobResponse) => {
    setSelectedJob(job);
  };

  const handleDelete = async (jobId: string) => {
    // Find and remove the item from favorites
    const item = items.find((i) => i.job?.job_id === jobId);
    if (item) {
      await handleUnlike(item.image_path);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[var(--bg-surface)]/95 backdrop-blur-sm border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                href="/"
                className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </Link>
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
                <h1 className="text-xl font-semibold">Favorites</h1>
              </div>
              <span className="text-[var(--text-muted)] text-sm">
                {total} {total === 1 ? 'image' : 'images'}
              </span>
            </div>

            <Link
              href="/feedback"
              className="px-4 py-2 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg text-sm hover:bg-[var(--bg-hover)] transition-colors"
            >
              Manage Feedback
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-[var(--text-muted)]">
            <svg className="w-20 h-20 opacity-20 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <p className="text-lg font-medium">No favorites yet</p>
            <p className="text-sm mt-1">Like images to add them here</p>
            <Link
              href="/"
              className="mt-4 px-4 py-2 bg-[var(--accent)] text-black font-medium rounded-lg hover:bg-[var(--accent-hover)] transition-colors"
            >
              Browse Gallery
            </Link>
          </div>
        ) : (
          <>
            {/* Gallery Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {items.map((item) => (
                <div
                  key={item.feedback_id}
                  className="group relative aspect-square bg-[var(--bg-elevated)] rounded-xl overflow-hidden border border-[var(--border-subtle)] hover:border-[var(--accent)]/50 transition-all cursor-pointer"
                  onClick={() => item.job && setSelectedJob(item.job)}
                >
                  {/* Image */}
                  <LazyImage
                    src={api.getThumbnailUrl(item.image_path)}
                    fallbackSrc={api.getImageUrl(item.image_path)}
                    alt={item.job?.prompt || ''}
                    className="w-full h-full transition-transform group-hover:scale-105"
                  />

                  {/* Heart button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnlike(item.image_path);
                    }}
                    className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-red-500/80 rounded-full transition-colors cursor-pointer"
                    title="Remove from favorites"
                  >
                    <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  </button>

                  {/* Hover overlay with prompt */}
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs line-clamp-2">
                      {item.job?.prompt || 'Unknown prompt'}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => fetchFavorites(true)}
                  className="px-6 py-2 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Image Modal */}
      {selectedJob && (
        <ImageModal
          job={selectedJob}
          jobs={allJobs}
          onClose={() => setSelectedJob(null)}
          onReuseParams={() => {}}
          onDelete={handleDelete}
          onNavigate={handleNavigate}
        />
      )}
    </div>
  );
}
